import { useState, useCallback, useRef, useEffect } from "react";

export interface UseResizablePanelOptions {
  /** Initial width of the panel in pixels */
  defaultWidth: number;
  /** Minimum width the panel can be resized to (in pixels) */
  minWidth: number;
  /** Maximum width the panel can be resized to (in pixels) */
  maxWidth: number;
  /** Whether the panel is currently collapsed */
  isCollapsed?: boolean;
  /** Called when dragging starts */
  onResizeStart?: () => void;
  /** Called during resizing with the current width */
  onResize?: (width: number) => void;
  /** Called when dragging ends with the final width */
  onResizeEnd?: (width: number) => void;
}

export interface UseResizablePanelReturn {
  /** Current panel width in pixels */
  width: number;
  /** Whether the user is currently dragging */
  isDragging: boolean;
  /** Event handler to attach to the resize handle's onMouseDown */
  handleMouseDown: (e: React.MouseEvent) => void;
  /** Adjust the panel width by a pixel delta (positive = wider, negative = narrower). Used for keyboard resize. */
  resizeByDelta: (delta: number) => void;
}

/**
 * A hook for managing a horizontally resizable panel.
 *
 * Attaches mousemove/mouseup listeners to the document during drag
 * to ensure smooth resizing even when the cursor leaves the handle.
 *
 * Best practices implemented:
 * - Uses requestAnimationFrame for smooth 60fps resize updates
 * - Stores consumer callbacks in refs so document-level handlers are
 *   referentially stable and never detached mid-drag
 * - Tracks the latest width in a ref so handleMouseDown doesn't close
 *   over stale state and avoids recreating every frame
 * - Prevents text selection during drag via CSS cursor override
 * - Cleans up all listeners on unmount
 * - Constrains width within min/max bounds
 * - Respects collapsed state (returns 0 width when collapsed)
 */
export function useResizablePanel({
  defaultWidth,
  minWidth,
  maxWidth,
  isCollapsed = false,
  onResizeStart,
  onResize,
  onResizeEnd,
}: UseResizablePanelOptions): UseResizablePanelReturn {
  const [width, setWidth] = useState(defaultWidth);
  const [isDragging, setIsDragging] = useState(false);

  // --- Refs: keep consumer callbacks and config up to date without
  //     destabilising the document-level handlers.
  const onResizeRef = useRef(onResize);
  const onResizeEndRef = useRef(onResizeEnd);
  const onResizeStartRef = useRef(onResizeStart);
  const minRef = useRef(minWidth);
  const maxRef = useRef(maxWidth);
  useEffect(() => {
    onResizeRef.current = onResize;
    onResizeEndRef.current = onResizeEnd;
    onResizeStartRef.current = onResizeStart;
    minRef.current = minWidth;
    maxRef.current = maxWidth;
  });

  // Track latest committed width so handleMouseDown doesn't close over `width`
  const widthRef = useRef(defaultWidth);
  const setTrackedWidth = useCallback((w: number) => {
    widthRef.current = w;
    setWidth(w);
  }, []);

  // Refs to track drag state without causing re-renders
  const isDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const rafIdRef = useRef<number | null>(null);

  // clampWidth reads from refs — referentially stable forever
  const clampWidth = useCallback(
    (w: number) => Math.min(maxRef.current, Math.max(minRef.current, w)),
    [],
  );

  // --- Document-level handlers: depend only on clampWidth (stable when
  //     minWidth/maxWidth don't change), so they are never recreated mid-drag.

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDraggingRef.current) return;

      // Cancel any pending animation frame to avoid stacking
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }

      rafIdRef.current = requestAnimationFrame(() => {
        const delta = e.clientX - startXRef.current;
        const newWidth = clampWidth(startWidthRef.current + delta);
        setTrackedWidth(newWidth);
        onResizeRef.current?.(newWidth);
      });
    },
    [clampWidth, setTrackedWidth],
  );

  const handleMouseUp = useCallback(
    (e: MouseEvent) => {
      if (!isDraggingRef.current) return;

      // Cancel any pending animation frame
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }

      isDraggingRef.current = false;
      setIsDragging(false);

      // Remove the cursor override from body
      document.body.style.cursor = "";
      document.body.style.userSelect = "";

      // Calculate final width
      const delta = e.clientX - startXRef.current;
      const finalWidth = clampWidth(startWidthRef.current + delta);
      setTrackedWidth(finalWidth);
      onResizeEndRef.current?.(finalWidth);

      // Remove document-level listeners
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    },
    [clampWidth, setTrackedWidth, handleMouseMove],
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      // Only handle left mouse button
      if (e.button !== 0) return;

      e.preventDefault();

      isDraggingRef.current = true;
      startXRef.current = e.clientX;
      startWidthRef.current = widthRef.current;
      setIsDragging(true);

      // Override cursor on body to maintain col-resize even outside the handle
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";

      onResizeStartRef.current?.();

      // Attach listeners to document for global tracking
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [handleMouseMove, handleMouseUp],
  );

  /** Adjust the panel width by a pixel delta. Used for keyboard-driven resize. */
  const resizeByDelta = useCallback(
    (delta: number) => {
      const newWidth = clampWidth(widthRef.current + delta);
      setTrackedWidth(newWidth);
      onResizeRef.current?.(newWidth);
    },
    [clampWidth, setTrackedWidth],
  );

  // Cleanup on unmount — handlers are now stable so this effectively
  // only runs on unmount.
  useEffect(() => {
    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [handleMouseMove, handleMouseUp]);

  return {
    width: isCollapsed ? 0 : width,
    isDragging,
    handleMouseDown,
    resizeByDelta,
  };
}
